/**
 * V3 Migration Module
 *
 * Provides functions to migrate ChordCakeState from V2 to V3 format and to
 * validate V3 data, plus format version detection shared by every version.
 * Used by all three apps and the Buffet backend. V3 → V4 lives in
 * `v4Migration.ts`.
 */
import { isKeySignature, isNote } from './noteTypes.js';
import { migrateChordToV3 } from './chordModelMigration.js';
import { normalizeSectionNote, normalizeSectionRepeat } from './sectionMarkers.js';
import { normalizeSongTempo, normalizeSongTimeSignature, VALID_TIME_SIGNATURE_DENOMINATORS, } from './songMeta.js';
// ============================================================================
// Valid ChordQuality values (for runtime validation)
// ============================================================================
export const VALID_QUALITIES = [
    'major',
    'minor',
    'diminished',
    'augmented',
    'sus2',
    'sus4',
    'minorMajor',
    'power',
];
// Defined next to the song-level time signature helpers; re-exported here
// because this is where the row validator has always published it.
export { VALID_TIME_SIGNATURE_DENOMINATORS };
// ============================================================================
// Version Detection
// ============================================================================
/**
 * Detect the format version from raw JSON data: the integer `version` field
 * when there is one (2 or more), else 1 (V1 files carried no version).
 *
 * Returns the number as written, including versions this package does not
 * know yet, so a reader can tell "newer than me" (see `upgradeToLatest`)
 * from "legacy".
 */
export function detectVersion(data) {
    const version = data?.version;
    return typeof version === 'number' && Number.isInteger(version) && version >= 2 ? version : 1;
}
// ============================================================================
// State-Level Migration (V2 → V3)
// ============================================================================
/**
 * Migrate a V2 ChordCakeState to V3.
 *
 * Handles all field renames, quality normalization, and sentinel cleanup:
 * - songTitle → title
 * - selectedKey → key (with selectedKeyDisplay fallback)
 * - selectedGuitarShapes → guitarShapes (defaults to {} if absent)
 * - selectedKeyDisplay removed
 * - Chord models migrated via migrateChordToV3
 * - customText/lyrics undefined → null
 *
 * @param v2 - V2 format state
 * @returns V3 format state
 */
export function migrateStateV2ToV3(v2) {
    return {
        version: 3,
        title: v2.songTitle ?? '',
        key: v2.selectedKey ?? v2.selectedKeyDisplay ?? null,
        guitarShapes: v2.selectedGuitarShapes ?? {},
        sections: v2.sections.map(section => ({
            id: section.id,
            title: section.title,
            rows: section.rows.map(row => ({
                id: row.id,
                slots: row.slots.map(migrateSlotV2ToV3),
                timeSignature: null,
            })),
        })),
    };
}
/**
 * Migrate a single V2 slot to V3 format.
 */
function migrateSlotV2ToV3(slot) {
    return {
        id: slot.id,
        chord: slot.chord ? migrateChordToV3(slot.chord) : null,
        customText: slot.customText ?? null,
        lyrics: slot.lyrics ?? null,
    };
}
// ============================================================================
// V3 Validation
// ============================================================================
/**
 * Validate that a state object conforms to ChordCakeStateV3 format,
 * normalizing it in place as it goes: optional fields that JSON.stringify
 * drops when undefined (slot customText/lyrics/chord, row timeSignature)
 * are backfilled to null on the input object. Section repeat/note are left
 * absent when missing and cleaned up rather than rejected when present (see
 * sectionMarkers.ts); everything else throws descriptive errors on invalid data.
 *
 * Unknown fields anywhere are left untouched, so additive fields survive.
 *
 * Because it mutates its argument, do not pass frozen objects.
 *
 * @param state - Data to validate and normalize (mutated in place)
 * @throws Error if validation fails
 */
export function validateAndNormalizeStateV3(state) {
    if (state.version !== 3) {
        throw new Error('Expected version: 3');
    }
    validateAndNormalizeSongShell(state, (row, path) => {
        if (!Array.isArray(row.slots)) {
            throw new Error(`Invalid ${path}.slots: expected array`);
        }
        for (let sli = 0; sli < row.slots.length; sli++) {
            validateSlotV3(row.slots[sli], `${path}.slot[${sli}]`);
        }
    });
}
/**
 * The song-level checks every version from V3 on shares: title, key, guitar
 * shapes, sections, tempo, global time signature, section repeat/note, row
 * ids and row time signatures. `validateRowContent` checks what the row
 * holds (slots in V3, bars in V4). Not part of the public API.
 */
export function validateAndNormalizeSongShell(state, validateRowContent) {
    if (typeof state.title !== 'string') {
        throw new Error('Invalid title: expected string');
    }
    if (state.key !== null) {
        if (!isKeySignature(state.key)) {
            throw new Error('Invalid key: expected KeySignature or null');
        }
    }
    if (typeof state.guitarShapes !== 'object' || state.guitarShapes === null || Array.isArray(state.guitarShapes)) {
        throw new Error('Invalid guitarShapes: expected object');
    }
    if (!Array.isArray(state.sections)) {
        throw new Error('Invalid sections: expected array');
    }
    // tempo and the global timeSignature are optional: absent stays absent,
    // present values are cleaned up leniently (a bad value becomes null)
    // rather than rejected.
    if (state.tempo !== undefined) {
        state.tempo = normalizeSongTempo(state.tempo);
    }
    if (state.timeSignature !== undefined) {
        state.timeSignature = normalizeSongTimeSignature(state.timeSignature);
    }
    // A global time signature replaces the per-row ones; drop row values that
    // slipped in alongside it (merged or hand-edited files).
    const hasGlobalTimeSignature = state.timeSignature != null;
    for (let si = 0; si < state.sections.length; si++) {
        const section = state.sections[si];
        if (typeof section.id !== 'string') {
            throw new Error(`Invalid section[${si}].id: expected string`);
        }
        if (typeof section.title !== 'string') {
            throw new Error(`Invalid section[${si}].title: expected string`);
        }
        if (!Array.isArray(section.rows)) {
            throw new Error(`Invalid section[${si}].rows: expected array`);
        }
        // repeat and note are optional: absent stays absent (no keys added to
        // every section of every file); present values are cleaned up leniently
        // (bad values become null or get clamped/trimmed) rather than rejected.
        if (section.repeat !== undefined) {
            section.repeat = normalizeSectionRepeat(section.repeat);
        }
        if (section.note !== undefined) {
            section.note = normalizeSectionNote(section.note);
        }
        for (let ri = 0; ri < section.rows.length; ri++) {
            const row = section.rows[ri];
            const path = `section[${si}].row[${ri}]`;
            if (typeof row.id !== 'string') {
                throw new Error(`Invalid ${path}.id: expected string`);
            }
            // timeSignature is optional. undefined and null are both treated as
            // "no time signature". If present and non-null, must be a valid object.
            if (row.timeSignature === undefined || hasGlobalTimeSignature) {
                row.timeSignature = null;
            }
            if (row.timeSignature !== null) {
                validateTimeSignatureV3(row.timeSignature, `${path}.timeSignature`);
            }
            validateRowContent(row, path);
        }
    }
}
/**
 * Validate a single V3 slot (the same fields a V4 slot has), backfilling
 * undefined fields to null. Not part of the public API.
 */
export function validateSlotV3(slot, path) {
    if (typeof slot !== 'object' || slot === null) {
        throw new Error(`Invalid ${path}: expected object`);
    }
    if (typeof slot.id !== 'string') {
        throw new Error(`Invalid ${path}.id: expected string`);
    }
    // Gracefully fallback undefined to null. JSON.stringify drops undefined fields
    // so an exported object might occasionally omit these fields instead of bringing them as null.
    if (slot.customText === undefined) {
        slot.customText = null;
    }
    // customText must be string | null (not undefined)
    if (slot.customText !== null && typeof slot.customText !== 'string') {
        throw new Error(`Invalid ${path}.customText: expected string or null`);
    }
    if (slot.lyrics === undefined) {
        slot.lyrics = null;
    }
    // lyrics must be string | null (not undefined)
    if (slot.lyrics !== null && typeof slot.lyrics !== 'string') {
        throw new Error(`Invalid ${path}.lyrics: expected string or null`);
    }
    if (slot.chord === undefined) {
        slot.chord = null;
    }
    if (slot.chord !== null) {
        validateChordV3(slot.chord, `${path}.chord`);
    }
}
/**
 * Validate a single V3 chord object.
 */
function validateChordV3(chord, path) {
    // root: Note | null
    if (chord.root !== null) {
        if (!isNote(chord.root)) {
            throw new Error(`Invalid ${path}.root: expected Note or null`);
        }
    }
    // Ensure no legacy 'fundamental' field
    if ('fundamental' in chord) {
        throw new Error(`Invalid ${path}: contains legacy 'fundamental' field, expected 'root'`);
    }
    // quality: ChordQuality | null
    if (chord.quality !== null) {
        if (typeof chord.quality !== 'string' || !VALID_QUALITIES.includes(chord.quality)) {
            throw new Error(`Invalid ${path}.quality: expected ChordQuality or null, got "${chord.quality}"`);
        }
    }
    // extension: string | null (never "-")
    if (chord.extension !== null && typeof chord.extension !== 'string') {
        throw new Error(`Invalid ${path}.extension: expected string or null`);
    }
    if (chord.extension === '-') {
        throw new Error(`Invalid ${path}.extension: "-" is not allowed in V3, use null`);
    }
    // isDominant: boolean
    if (typeof chord.isDominant !== 'boolean') {
        throw new Error(`Invalid ${path}.isDominant: expected boolean`);
    }
    // adds: string[] (no "-" sentinel)
    if (!Array.isArray(chord.adds)) {
        throw new Error(`Invalid ${path}.adds: expected array`);
    }
    if (chord.adds.includes('-')) {
        throw new Error(`Invalid ${path}.adds: contains "-" sentinel, not allowed in V3`);
    }
    // alterations: string[] (no "-" sentinel)
    if (!Array.isArray(chord.alterations)) {
        throw new Error(`Invalid ${path}.alterations: expected array`);
    }
    if (chord.alterations.includes('-')) {
        throw new Error(`Invalid ${path}.alterations: contains "-" sentinel, not allowed in V3`);
    }
    // bass: Note | null
    if (chord.bass !== null) {
        if (!isNote(chord.bass)) {
            throw new Error(`Invalid ${path}.bass: expected Note or null`);
        }
    }
}
/**
 * Validate a single V3 time signature object.
 *
 * `numerator` must be a positive integer. `denominator` must be one of
 * the canonical powers of two listed in VALID_TIME_SIGNATURE_DENOMINATORS.
 */
function validateTimeSignatureV3(ts, path) {
    if (typeof ts !== 'object' || ts === null || Array.isArray(ts)) {
        throw new Error(`Invalid ${path}: expected object or null`);
    }
    if (typeof ts.numerator !== 'number' || !Number.isInteger(ts.numerator) || ts.numerator < 1) {
        throw new Error(`Invalid ${path}.numerator: expected positive integer`);
    }
    if (typeof ts.denominator !== 'number' || !VALID_TIME_SIGNATURE_DENOMINATORS.includes(ts.denominator)) {
        throw new Error(`Invalid ${path}.denominator: expected one of ${VALID_TIME_SIGNATURE_DENOMINATORS.join(', ')}`);
    }
}
